import { useEffect, useRef, useState } from 'react'
import { BrowserMultiFormatReader } from '@zxing/browser'
import type { IScannerControls } from '@zxing/browser'
import { NotFoundException, DecodeHintType } from '@zxing/library'

interface Props {
  onScan: (barcode: string) => void
  onClose: () => void
}

export function BarcodeScanner({ onScan, onClose }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const controlsRef = useRef<IScannerControls | null>(null)
  const [error, setError] = useState<string | null>(null)
  const scannedRef = useRef(false)

  useEffect(() => {
    const hints = new Map()
    hints.set(DecodeHintType.TRY_HARDER, true)
    const reader = new BrowserMultiFormatReader(hints)

    reader
      .decodeFromConstraints(
        { video: { facingMode: 'environment' } },
        videoRef.current!,
        (result, err, controls) => {
          controlsRef.current = controls
          if (result && !scannedRef.current) {
            scannedRef.current = true
            controls?.stop()
            onScan(result.getText())
          }
          if (err && !(err instanceof NotFoundException)) {
            // NotFoundException fires on every frame without a barcode — normal
          }
        }
      )
      .catch((e) => {
        setError(e?.message ?? 'Camera not available')
      })

    return () => {
      controlsRef.current?.stop()
    }
  }, [onScan])

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: '#000',
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0.75rem 1rem',
          paddingTop: 'calc(0.75rem + env(safe-area-inset-top))',
          background: 'rgba(0,0,0,0.7)',
          zIndex: 10,
        }}
      >
        <span style={{ color: '#fafafa', fontWeight: 600, fontSize: '0.95rem' }}>
          Scan barcode
        </span>
        <button
          onClick={onClose}
          style={{
            background: 'rgba(255,255,255,0.15)',
            border: 'none',
            borderRadius: 20,
            color: '#fafafa',
            fontSize: '0.85rem',
            padding: '0.35rem 0.75rem',
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
      </div>

      {/* Camera view */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        {error ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#fafafa',
              padding: '2rem',
              textAlign: 'center',
              gap: '1rem',
            }}
          >
            <span style={{ fontSize: '2rem' }}>📷</span>
            <p style={{ color: '#a1a1aa', fontSize: '0.9rem' }}>{error}</p>
            <p style={{ color: '#71717a', fontSize: '0.8rem' }}>
              Allow camera access in your browser settings and try again.
            </p>
          </div>
        ) : (
          <>
            <video
              ref={videoRef}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />

            {/* Viewfinder overlay */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
              }}
            >
              {/* Dark surround */}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'rgba(0,0,0,0.45)',
                  maskImage:
                    'radial-gradient(ellipse 70% 25% at 50% 50%, transparent 100%, black 100%)',
                  WebkitMaskImage:
                    'radial-gradient(ellipse 70% 25% at 50% 50%, transparent 100%, black 100%)',
                }}
              />

              {/* Corner brackets */}
              <div style={{ position: 'relative', width: 240, height: 120 }}>
                {['topleft', 'topright', 'bottomleft', 'bottomright'].map((corner) => (
                  <div
                    key={corner}
                    style={{
                      position: 'absolute',
                      width: 24,
                      height: 24,
                      borderColor: '#22d3ee',
                      borderStyle: 'solid',
                      borderWidth: 0,
                      ...(corner === 'topleft' && {
                        top: 0,
                        left: 0,
                        borderTopWidth: 3,
                        borderLeftWidth: 3,
                        borderRadius: '4px 0 0 0',
                      }),
                      ...(corner === 'topright' && {
                        top: 0,
                        right: 0,
                        borderTopWidth: 3,
                        borderRightWidth: 3,
                        borderRadius: '0 4px 0 0',
                      }),
                      ...(corner === 'bottomleft' && {
                        bottom: 0,
                        left: 0,
                        borderBottomWidth: 3,
                        borderLeftWidth: 3,
                        borderRadius: '0 0 0 4px',
                      }),
                      ...(corner === 'bottomright' && {
                        bottom: 0,
                        right: 0,
                        borderBottomWidth: 3,
                        borderRightWidth: 3,
                        borderRadius: '0 0 4px 0',
                      }),
                    }}
                  />
                ))}

                {/* Scan line animation */}
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    height: 2,
                    background: 'linear-gradient(to right, transparent, #22d3ee, transparent)',
                    animation: 'scanline 2s linear infinite',
                    top: '50%',
                  }}
                />
              </div>
            </div>

            <p
              style={{
                position: 'absolute',
                bottom: 'calc(2rem + env(safe-area-inset-bottom))',
                left: 0,
                right: 0,
                textAlign: 'center',
                color: 'rgba(255,255,255,0.7)',
                fontSize: '0.8rem',
              }}
            >
              Point camera at a barcode
            </p>
          </>
        )}
      </div>

      <style>{`
        @keyframes scanline {
          0%   { top: 10%; }
          50%  { top: 90%; }
          100% { top: 10%; }
        }
      `}</style>
    </div>
  )
}
